/**
 * Projects page - browse, search, and filter the project pipeline.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Loader2,
  Plus,
  Briefcase,
  Search,
  Layers,
  DollarSign,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import projectService from '../services/projectService';
import {
  Project,
  ProjectListFilters,
  PipelineStatus,
  PocStatus,
} from '../types/project';

const PIPELINE_OPTIONS: Array<{ value: PipelineStatus; label: string }> = [
  { value: 'seeking', label: 'Seeking' },
  { value: 'negotiating', label: 'Negotiating' },
  { value: 'term_sheet', label: 'Term Sheet' },
  { value: 'closing', label: 'Closing' },
  { value: 'closed', label: 'Closed' },
  { value: 'passed', label: 'Passed' },
];

const POC_OPTIONS: Array<{ value: PocStatus; label: string }> = [
  { value: 'concept', label: 'Concept' },
  { value: 'prototype', label: 'Prototype' },
  { value: 'pilot', label: 'Pilot' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'scaling', label: 'Scaling' },
];

const pipelineBadge = (status?: PipelineStatus): string => {
  switch (status) {
    case 'closed':
      return 'bg-green-100 text-green-800';
    case 'closing':
    case 'term_sheet':
      return 'bg-emerald-100 text-emerald-800';
    case 'negotiating':
      return 'bg-yellow-100 text-yellow-800';
    case 'passed':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-blue-100 text-blue-700';
  }
};

const pocBadge = (status?: PocStatus): string => {
  switch (status) {
    case 'scaling':
      return 'bg-purple-100 text-purple-800';
    case 'revenue':
      return 'bg-green-100 text-green-800';
    case 'pilot':
      return 'bg-blue-100 text-blue-800';
    case 'prototype':
      return 'bg-indigo-100 text-indigo-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatLabel = (value?: string): string => {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatMoney = (value?: number): string => {
  if (value === undefined || value === null) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const progressPercent = (committed?: number, needed?: number): number => {
  if (!needed || needed <= 0) return 0;
  const raw = ((committed || 0) / needed) * 100;
  return Math.max(0, Math.min(100, raw));
};

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchInput, setSearchInput] = useState<string>('');
  const [filters, setFilters] = useState<ProjectListFilters>({});

  const queryFilters: ProjectListFilters = useMemo(() => filters, [filters]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const result = await projectService.fetchProjects(queryFilters);
      if (result.error) {
        toast.error(result.error);
        setProjects([]);
      } else {
        setProjects(result.data?.projects || []);
      }
      setIsLoading(false);
    };
    loadData();
  }, [queryFilters]);

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput || undefined }));
  };

  const handleFilterChange = <K extends keyof ProjectListFilters>(
    key: K,
    value: ProjectListFilters[K] | ''
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
    }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
            <p className="text-gray-600 mt-1">
              Funding initiatives — track pipeline, traction, and instrument fit.
            </p>
          </div>
          <Link
            to="/projects/new"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add project
          </Link>
        </div>

        {/* Search + filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by project name or description..."
                className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              Search
            </button>
          </form>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select
              value={filters.pipeline_status || ''}
              onChange={(e) =>
                handleFilterChange('pipeline_status', e.target.value as PipelineStatus | '')
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All pipeline stages</option>
              {PIPELINE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={filters.poc_status || ''}
              onChange={(e) =>
                handleFilterChange('poc_status', e.target.value as PocStatus | '')
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All traction levels</option>
              {POC_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={
                filters.is_standalone === undefined
                  ? ''
                  : filters.is_standalone
                  ? 'true'
                  : 'false'
              }
              onChange={(e) => {
                const v = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  is_standalone: v === '' ? undefined : v === 'true',
                }));
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All projects</option>
              <option value="true">Standalone only</option>
              <option value="false">Linked to startup</option>
            </select>

            <input
              type="number"
              min="1"
              placeholder="Startup ID"
              value={filters.startup_id ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  startup_id: v === '' ? undefined : parseInt(v, 10),
                }));
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading projects...
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
            <Layers className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No projects found</h2>
            <p className="text-sm">
              Try adjusting filters, or add your first project.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {projects.map((project) => {
              const pct = progressPercent(project.funding_committed, project.funding_needed);
              return (
                <button
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-md transition-all space-y-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900 truncate">
                        {project.project_name}
                      </h2>
                      <p className="text-xs text-gray-600">
                        {project.is_standalone
                          ? 'Standalone project'
                          : project.startup_id
                          ? `Startup #${project.startup_id}`
                          : 'Unassigned'}
                      </p>
                    </div>
                    {project.pipeline_status && (
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${pipelineBadge(
                          project.pipeline_status
                        )}`}
                      >
                        {formatLabel(project.pipeline_status)}
                      </span>
                    )}
                  </div>

                  {project.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
                  )}

                  {/* Funding progress */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span className="inline-flex items-center">
                        <DollarSign className="w-3 h-3 mr-1" />
                        {formatMoney(project.funding_committed)} of{' '}
                        {formatMoney(project.funding_needed)}
                      </span>
                      <span className="font-medium text-gray-700">{pct.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {project.poc_status && (
                      <span
                        className={`px-2 py-0.5 font-medium rounded-full ${pocBadge(
                          project.poc_status
                        )}`}
                      >
                        {formatLabel(project.poc_status)}
                      </span>
                    )}
                    {project.selected_instrument && (
                      <span className="inline-flex items-center bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                        <Briefcase className="w-3 h-3 mr-1" />
                        {formatLabel(project.selected_instrument)}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProjectsPage;
